/**
 * TimeToggle Component
 *
 * Toggle between Today, Yesterday, and Custom date modes.
 * All modes fetch from daily report API with different date parameters.
 */

import React from 'react'
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { subDays } from 'date-fns'
import { TimeMode } from '../../../../services/reportsApi'

interface TimeToggleProps {
  mode: TimeMode
  customDate: Date | null
  onModeChange: (mode: TimeMode) => void
  onDateChange: (date: Date | null) => void
}

const TimeToggle: React.FC<TimeToggleProps> = ({
  mode,
  customDate,
  onModeChange,
  onDateChange
}) => {
  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: TimeMode | null
  ) => {
    if (newMode !== null) {
      onModeChange(newMode)
    }
  }

  const handleDateChange = (date: Date | null) => {
    onDateChange(date)
    if (date) {
      onModeChange('custom')
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        size="small"
        aria-label="time mode"
      >
        <Tooltip title="Today's data up to current time">
          <ToggleButton value="today" aria-label="today">
            Today
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Yesterday's complete data">
          <ToggleButton value="yesterday" aria-label="yesterday">
            Yesterday
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Select a specific date">
          <ToggleButton value="custom" aria-label="custom date">
            Date
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      {mode === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={customDate}
            onChange={handleDateChange}
            maxDate={subDays(new Date(), 1)}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 160 }
              }
            }}
          />
        </LocalizationProvider>
      )}
    </Box>
  )
}

export default TimeToggle
